import { type ErrorCode, type Result } from '@global-client/models/MSS/TGeneral';

type AttrSource = {
	Yivi: {
		attr_type_id: string;
	};
};

export type AttrType = {
	id: string;
	handles: string[];
	bannable: boolean;
	identifying: boolean;
	sources: AttrSource[];
};

export type WelcomeResp = { attr_types: Record<string, AttrType> };

export type AuthsWelcomeResp = Result<WelcomeResp, ErrorCode>;

export enum Source {
	Yivi = 'Yivi',
}

export type LoginMethod = {
	readonly source: Source;
	readonly attr_types: readonly string[];
	readonly identifying_attr: string[];
	readonly login_attr: string[];
	readonly register_attr: string[];
};

export const loginMethods: Record<string, LoginMethod> = {
	Yivi: {
		source: Source.Yivi,
		attr_types: ['email', 'phone', 'ph_card'],
		identifying_attr: ['email', 'ph_card'],
		login_attr: ['ph_card'],
		register_attr: ['email', 'phone'],
	},
} as const;

export type AuthStartReq = {
	source: Source;
	attr_types: readonly string[];
	attr_type_choices: string[][];
	yivi_chained_session: boolean;
	yivi_chained_session_drip: boolean;
};

type AuthTask = {
	Yivi: {
		disclosure_request: string;
		yivi_requestor_url: string;
	};
};
export type YiviWaitForResultResp = { Success: { disclosure: string } } | 'PleaseRestartAuth' | 'SessionGone';

export type YiviReleaseNextSessionResp = { Success: Record<string, never> } | 'PleaseRestartAuth' | 'SessionGone' | 'YiviServerGone' | 'TooEarly';

export type YiviReleaseNextSessionReq = { state: number[]; next_session?: string; stale_after?: number };

export type CardResp = { Success: CardRespSuccess } | 'PleaseRetryWithNewCardPseud';

export type CardRespSuccess = { attr: string; issuance_request: string; yivi_requestor_url: string };

export type CardReq = { card_pseud_package: { card_pseud: number; registration_date?: number }; comment: string };

export type StartResp =
	{ Success: StartRespSuccess } | { UnknownAttrType: string } | { SourceNotAvailableFor: string } | 'ChainedSessionsTemporarilyUnavailable';

export type StartRespSuccess = { task: AuthTask; state: Array<number> };

export type AuthStartResp = Result<StartResp, ErrorCode>;

export type AuthProof = {
	Yivi: {
		disclosure: string;
	};
};

export type AuthCompleteReq = {
	proof: AuthProof;
	state: Array<number>;
};

export type CompleteResp = { Success: { attrs: Record<string, string> } } | 'PleaseRestartAuth';

export type AuthCompleteResp = Result<CompleteResp, ErrorCode>;

export type SuccesResp = { attrs: Record<string, string> };

export type AttrKeyReq = {
	attr: string;
	timestamp: string | null;
};

export type AuthAttrKeyReq = Record<string, AttrKeyReq>;

export type AttrKeyResp = {
	latest_key: [string, string];
	old_key: string | null;
};

export type AttrKeysResp = { RetryWithNewAttr: string } | { Success: Record<string, AttrKeyResp> };

export type AuthAttrKeysResp = Result<AttrKeysResp, ErrorCode>;
